import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  Avatar
} from '@mui/material';
import {
  Save as SaveIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DatabaseService from '../services/database';
import { processImageFile } from '../utils/imageUtils';
import { handleNotesPaste } from '../utils/pastePlainText';
import { useLicense } from '../context/LicenseContext';
import RaceResults from '../components/RaceResults';

// Module-scope so its identity is stable across AddBird re-renders — otherwise
// every keystroke in the form would remount the card and re-decode the
// (potentially multi-MB) photo data URL.
const PhotoUploadCard = ({ label, value, onUpload, onRemove }) => (
  <Card variant="outlined">
    {value ? (
      <CardMedia
        component="img"
        height="180"
        image={value}
        alt={label}
        sx={{ objectFit: 'cover' }}
      />
    ) : (
      <Box height={180} display="flex" alignItems="center" justifyContent="center" bgcolor="grey.100">
        <PhotoCameraIcon sx={{ fontSize: 48, color: 'grey.400' }} />
      </Box>
    )}
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
      <Box display="flex" gap={1}>
        <Button size="small" variant="outlined" component="label" startIcon={<PhotoCameraIcon />}>
          {value ? 'Replace' : 'Upload'}
          <input type="file" accept="image/*" hidden onChange={onUpload} />
        </Button>
        {value && (
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={onRemove}>
            Remove
          </Button>
        )}
      </Box>
    </CardContent>
  </Card>
);

const AddBird = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEdit = Boolean(id);
  const { state: licenseState, openPrompt } = useLicense();
  const [birdCount, setBirdCount] = useState(null);
  const submitting = useRef(false);
  const errorRef = useRef(null);

  const readonly = licenseState?.mode === 'readonly' || licenseState?.mode === 'unlicensed';
  const atTrialCap = Boolean(
    !isEdit &&
    licenseState?.mode === 'trial' &&
    licenseState.trial &&
    birdCount !== null &&
    birdCount >= licenseState.trial.maxBirds
  );
  const initialForm = () => ({
    name: '',
    ring_letters: '',
    ring_numbers: '',
    ring_number: '',
    sex: '',
    color: '',
    breed: '',
    year: new Date().getFullYear(),
    notes: '',
    body_photo: null,
    eye_photo: null,
    sire_id: null,
    dam_id: null
  });
  const [formData, setFormData] = useState(initialForm());
  const [availableBirds, setAvailableBirds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'center' });
  }, [error]);

  const splitRingNumber = (ringNumber) => {
    const match = (ringNumber || '').match(/^([A-Za-z]*)\s?(.*)$/);
    return {
      letters: match ? match[1] : '',
      numbers: match ? match[2] : ''
    };
  };

  useEffect(() => {
    loadAvailableBirds();
    if (isEdit) {
      // Load existing bird data into form
      (async () => {
        try {
          const existing = await DatabaseService.getBirdById(id);
          if (existing) {
            const { letters, numbers } = splitRingNumber(existing.ring_number);
            setFormData({
              name: existing.name || '',
              ring_letters: letters,
              ring_numbers: numbers,
              ring_number: existing.ring_number || '',
              sex: existing.sex || '',
              color: existing.color || '',
              breed: existing.breed || '',
              year: existing.year || new Date().getFullYear(),
              notes: existing.notes || '',
              body_photo: existing.body_photo || null,
              eye_photo: existing.eye_photo || null,
              sire_id: existing.sire_id || null,
              dam_id: existing.dam_id || null,
            });
          }
        } catch (e) {
          console.error('Error loading bird for edit:', e);
          setError('Failed to load bird for editing');
        }
      })();
    } else if (location.state?.duplicateFrom) {
      // "Duplicate" from a bird profile: pre-fill shared details (parents,
      // strain, color, year, ring letters) but leave identity fields blank.
      const source = location.state.duplicateFrom;
      const { letters } = splitRingNumber(source.ring_number);
      setFormData({
        ...initialForm(),
        ring_letters: letters,
        color: source.color || '',
        breed: source.breed || '',
        year: source.year || new Date().getFullYear(),
        sire_id: source.sire_id || null,
        dam_id: source.dam_id || null,
        // Leave ring_number empty until the user enters the digits — prefilling
        // it with just the letters would save a meaningless ring like "BE".
        ring_number: ''
      });
      setError('');
      setSuccess('');
    } else {
      // Navigated to Add page: reset the form completely
      setFormData(initialForm());
      setError('');
      setSuccess('');
      setLoading(false);
    }
  }, [id, location.state]);

  const loadAvailableBirds = async () => {
    try {
      const birds = await DatabaseService.getBirdsForAutocomplete();
      setAvailableBirds(birds);
      const count = await DatabaseService.getBirdCount();
      setBirdCount(count);
    } catch (error) {
      console.error('Error loading birds for autocomplete:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      // Auto-combine ring letters and numbers into ring_number with a space in between
      if (field === 'ring_letters' || field === 'ring_numbers') {
        const letters = field === 'ring_letters' ? value : prev.ring_letters;
        const numbers = field === 'ring_numbers' ? value : prev.ring_numbers;
        // Only add a space if both letters and numbers are present
        updated.ring_number = letters && numbers ?
          `${letters} ${numbers}` :
          `${letters}${numbers}`;
      }

      return updated;
    });
    setError('');
  };

  const handleYearChange = (rawValue) => {
    if (rawValue === '') {
      handleInputChange('year', '');
      return;
    }
    const parsed = parseInt(rawValue, 10);
    handleInputChange('year', Number.isNaN(parsed) ? '' : parsed);
  };

  const handleParentSelect = (field, bird) => {
    setFormData(prev => ({
      ...prev,
      [field]: bird ? bird.id : null
    }));
  };

  const handlePhotoUpload = async (field, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await processImageFile(file);
      setFormData(prev => ({ ...prev, [field]: dataUrl }));
      setError('');
    } catch (err) {
      setError(err.message || 'Could not process the selected image.');
    }
  };

  const handlePhotoRemove = (field) => {
    setFormData(prev => ({ ...prev, [field]: null }));
  };

  const validateForm = async () => {
    // Check for duplicate only when ring_letters + ring_numbers + year all match
    if (formData.ring_letters && formData.ring_numbers && formData.year) {
      const isDuplicate = await DatabaseService.checkDuplicateRingNumber(
        formData.ring_letters.toUpperCase(),
        formData.ring_numbers,
        formData.year,
        isEdit ? id : null
      );

      if (isDuplicate) {
        setError(`A pigeon with ring letters "${formData.ring_letters.toUpperCase()}", ring numbers "${formData.ring_numbers}", and year "${formData.year}" already exists.`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current || readonly || atTrialCap) return;
    submitting.current = true;
    setLoading(true);
    setError('');

    try {
      if (!(await validateForm())) return;
      if (isEdit) {
        await DatabaseService.updateBird(id, formData);
        navigate(`/birds/${id}`);
      } else {
        await DatabaseService.createBird(formData);
        navigate('/birds');
      }
    } catch (error) {
      console.error('Error creating pigeon:', error);
      if (error.isLicenseError) {
        setError(error.message);
      } else {
        setError(isEdit ? 'Error updating pigeon. Please try again.' : 'Error creating pigeon. Please try again.');
      }
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(`/birds/${id}`);
    } else {
      navigate('/birds');
    }
  };

  const getMaleOptions = () => availableBirds.filter(bird => bird.sex === 'male' && String(bird.id) !== String(id));
  const getFemaleOptions = () => availableBirds.filter(bird => bird.sex === 'female' && String(bird.id) !== String(id));

  return (
    <Box sx={{ pb: { xs: 12, md: 0 } }}>
      <Typography variant="h4" component="h1" fontWeight="bold" mb={2}>
        {isEdit ? 'Edit Pigeon' : 'Add New Pigeon'}
      </Typography>

      {readonly && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={<Button color="inherit" size="small" onClick={openPrompt}>{licenseState?.mode === 'unlicensed' ? 'Start free trial' : 'Reactivate'}</Button>}
        >
          {licenseState?.mode === 'unlicensed'
            ? 'Start your free 7-day trial to save birds and create pedigrees. No card required.'
            : 'Your plan has ended. Choose a plan to add or edit birds.'}
        </Alert>
      )}
      {atTrialCap && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={<Button color="inherit" size="small" onClick={openPrompt}>Upgrade</Button>}
        >
          You've reached the free trial limit of {licenseState.trial.maxBirds} birds. Choose a paid plan to keep growing your loft.
        </Alert>
      )}

      {error && <Alert ref={errorRef} severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pigeon Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Optional - leave blank for unnamed pigeon"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Ring Letters"
                  value={formData.ring_letters}
                  onChange={(e) => handleInputChange('ring_letters', e.target.value.toUpperCase())}
                  placeholder="e.g., BE"
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Ring Numbers"
                  value={formData.ring_numbers}
                  onChange={(e) => handleInputChange('ring_numbers', e.target.value)}
                  placeholder="e.g., 2024-001"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Sex</InputLabel>
                  <Select
                    value={formData.sex}
                    label="Sex"
                    onChange={(e) => handleInputChange('sex', e.target.value)}
                  >
                    <MenuItem value="male">
                      <Box display="flex" alignItems="center">
                        <MaleIcon sx={{ mr: 1 }} />
                        Cock
                      </Box>
                    </MenuItem>
                    <MenuItem value="female">
                      <Box display="flex" alignItems="center">
                        <FemaleIcon sx={{ mr: 1 }} />
                        Hen
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Color"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  placeholder="e.g., Blue Bar, Red Checker"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Strain"
                  value={formData.breed}
                  onChange={(e) => handleInputChange('breed', e.target.value)}
                  placeholder="e.g., Janssen, Sion"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={formData.year}
                  onChange={(e) => handleYearChange(e.target.value)}
                  inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                />
              </Grid>

              {/* Photos */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                  Photos
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Photos appear on the bird profile and exported pedigree PDFs.
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <PhotoUploadCard
                  label="Body Photo"
                  value={formData.body_photo}
                  onUpload={(e) => handlePhotoUpload('body_photo', e)}
                  onRemove={() => handlePhotoRemove('body_photo')}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <PhotoUploadCard
                  label="Eye Photo"
                  value={formData.eye_photo}
                  onUpload={(e) => handlePhotoUpload('eye_photo', e)}
                  onRemove={() => handlePhotoRemove('eye_photo')}
                />
              </Grid>

              {/* Parent Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                  Parents (optional)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select parents already saved in your loft. You can leave unknown parents blank.
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={getMaleOptions()}
                  value={availableBirds.find(bird => bird.id === formData.sire_id) || null}
                  getOptionLabel={(option) => `${option.name || option.ring_number || 'Unnamed Bird'} (${option.ring_number || 'No ring details'})`}
                  onChange={(event, newValue) => handleParentSelect('sire_id', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sire (Father)"
                      placeholder="Search for male birds..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Avatar sx={{ mr: 2, bgcolor: 'info.main' }}>
                        <MaleIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body1">{option.name || option.ring_number || 'Unnamed Bird'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.ring_number || 'No ring details'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={getFemaleOptions()}
                  value={availableBirds.find(bird => bird.id === formData.dam_id) || null}
                  getOptionLabel={(option) => `${option.name || option.ring_number || 'Unnamed Bird'} (${option.ring_number || 'No ring details'})`}
                  onChange={(event, newValue) => handleParentSelect('dam_id', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dam (Mother)"
                      placeholder="Search for female birds..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Avatar sx={{ mr: 2, bgcolor: 'secondary.main' }}>
                        <FemaleIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body1">{option.name || option.ring_number || 'Unnamed Bird'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.ring_number || 'No ring details'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={4}
                  InputProps={{
                    sx: {
                      alignItems: 'flex-start',
                      '& textarea': {
                        resize: 'vertical',
                        overflow: 'auto',
                        minHeight: 96
                      }
                    }
                  }}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  onPaste={(e) => handleNotesPaste(e, formData.notes, (v) => handleInputChange('notes', v))}
                  placeholder="Additional notes about this bird..."
                />
              </Grid>

              {/* Messages */}
              {success && (
                <Grid item xs={12}>
                  <Alert severity="success">{success}</Alert>
                </Grid>
              )}

              {/* Actions */}
              <Grid item xs={12}>
                <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap" mt={2}
                  sx={{ position: { xs: 'fixed', md: 'static' }, bottom: 0, left: 0, right: 0,
                    zIndex: 1100, bgcolor: 'background.paper', p: { xs: 2, md: 0 },
                    pb: { xs: 'max(16px, env(safe-area-inset-bottom))', md: 0 },
                    borderTop: { xs: '1px solid', md: 'none' }, borderColor: 'divider' }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={loading}
                    sx={{ minHeight: 48 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={loading || readonly || atTrialCap}
                    sx={{ minHeight: 48, flexGrow: { xs: 1, sm: 0 } }}
                  >
                    {loading ? 'Saving...' : isEdit ? 'Update Bird' : 'Save Bird'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Race results are available after the pigeon has been saved. */}
      {isEdit && <Card sx={{ mt: 3 }}>
        <CardContent>
          <RaceResults birdId={id} readOnly={readonly} />
        </CardContent>
      </Card>}
    </Box>
  );
};

export default AddBird;
